import { IFileValidationResult, IFileValidationRules } from '../Types/assets';

// Re-export the types for easier importing
export type { IFileValidationResult, IFileValidationRules };

export const validateFileSize = (file: File, maxSize: number): IFileValidationResult => {
  const errors: string[] = [];

  if (file.size === 0) {
    errors.push('File cannot be empty');
  } else if (file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    errors.push(`File size exceeds maximum allowed limit of ${maxSizeMB}MB`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateFileType = (
  file: File, 
  allowedTypes: string[], 
  allowedExtensions?: string[]
): IFileValidationResult => {
  const errors: string[] = [];

  // Check MIME type first
  if (file.type && allowedTypes.includes(file.type)) {
    return { isValid: true, errors: [] };
  }

  // If MIME type is empty or not allowed, check file extension
  if (allowedExtensions && allowedExtensions.length > 0) {
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => 
      fileName.endsWith(ext.toLowerCase())
    );

    if (hasValidExtension) {
      return { isValid: true, errors: [] };
    }
  }

  // If we get here, the file type is not allowed
  if (file.type) {
    errors.push(`File type ${file.type} is not allowed`);
  } else if (allowedExtensions && allowedExtensions.length > 0) {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    errors.push(`File extension ${fileExtension} is not allowed`);
  } else {
    errors.push('File type is not supported');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateFile = (file: File, rules: IFileValidationRules): IFileValidationResult => {
  const errors: string[] = [];

  // Validate file size
  const sizeResult = validateFileSize(file, rules.maxSize);
  errors.push(...sizeResult.errors);

  // Validate file type
  const typeResult = validateFileType(file, rules.allowedTypes, rules.allowedExtensions);
  errors.push(...typeResult.errors);

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const getFileValidationRules = (file: File): IFileValidationRules => {
  // Determine the file type from the file
  const fileType = getFileTypeFromFile(file);
  
  switch (fileType.toLowerCase()) {
    case 'pdf':
      return {
        maxSize: 50 * 1024 * 1024, // 50MB
        allowedTypes: ['application/pdf'],
        allowedExtensions: ['.pdf']
      };
    
    case 'image':
      return {
        maxSize: 10 * 1024 * 1024, // 10MB
        allowedTypes: ['image/jpeg', 'image/jpg', 'image/png'],
        allowedExtensions: ['.jpg', '.jpeg', '.png']
      };

    // Reject video files for now
    // case 'video':
    //   return {
    //     maxSize: 100 * 1024 * 1024, // 100MB
    //     allowedTypes: ['video/mp4', 'video/mov', 'video/avi'],
    //     allowedExtensions: ['.mp4', '.mov', '.avi']
    //   };
    
    case 'document':
      return {
        maxSize: 25 * 1024 * 1024, // 25MB
        allowedTypes: [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'text/plain'
        ],
        allowedExtensions: ['.pdf', '.doc', '.docx', '.txt']
      };
    
    default:
      return {
        maxSize: 10 * 1024 * 1024, // 10MB default
        allowedTypes: [],
        allowedExtensions: []
      };
  }
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getFileIcon = (fileType: string): string => {
  if (fileType.startsWith('image/')) {
    return '🖼️';
  } else if (fileType.startsWith('video/')) {
    return '🎥';
  } else if (fileType === 'application/pdf') {
    return '📄';
  } else if (fileType.startsWith('text/')) {
    return '📝';
  } else {
    return '📁';
  }
};

export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/');
};

export const isVideoFile = (file: File): boolean => {
  return file.type.startsWith('video/');
};

export const isPdfFile = (file: File): boolean => {
  return file.type === 'application/pdf';
};

export const canPreviewFile = (file: File): boolean => {
  return isImageFile(file) || isPdfFile(file);
};

export const getFileTypeFromFile = (file: File): string => {
  // Check MIME type to determine file type category
  if (file.type === 'application/pdf') {
    return 'pdf';
  }
  if (file.type.startsWith('image/')) {
    return 'image';
  }
  if (file.type.startsWith('video/')) {
    return 'video';
  }
  // Check for document types
  if (
    file.type === 'application/msword' ||
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    file.type === 'text/plain'
  ) {
    return 'document';
  }
  // If MIME type is not available, try to infer from extension
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  if (extension === '.pdf') return 'pdf';
  if (['.jpg', '.jpeg', '.png', '.gif'].includes(extension)) return 'image';
  if (['.mp4', '.mov', '.avi'].includes(extension)) return 'video';
  if (['.doc', '.docx', '.txt'].includes(extension)) return 'document';
  
  // Default fallback
  return 'notAllowed';
};